import { ILineItem, IMatchedOrder } from "../types/claim";

/**
 * Patient responsibility extracted from 835 claim adjustments
 * Amounts are in dollars (from 835 EDI data)
 */
export interface IClaimPatientResponsibility {
  deductible: number; // PR adjustment code 1
  coinsurance: number; // PR adjustment code 2
  copay: number; // PR adjustment code 3
  totalOOP: number; // Sum of all PR adjustments
}

/**
 * Resolve the effective line items for a claim, episode-aware.
 *
 * CLE-2166 episode roots leave `lineItems` null and store the rolled-up lines on
 * `episodeNetLineItems`; pre-CLE-2166 flat claims keep them on `lineItems`. Reading
 * `lineItems` directly on an episode root yields null → blank patient-responsibility
 * columns (CLE-2512). Mirror the backend `getEffectiveLineItems`/`effectiveLineItemsForClaim`
 * and the ClaimDetailsModal so the list, export, and detail views all agree.
 */
export function getEffectiveClaimLineItems(claim: {
  isEpisodeRoot?: boolean;
  episodeNetLineItems?: ILineItem[] | null;
  lineItems?: ILineItem[] | null;
}): ILineItem[] {
  if (claim.isEpisodeRoot && claim.episodeNetLineItems) {
    return claim.episodeNetLineItems;
  }
  return claim.lineItems || [];
}

/** Sum PR-1 / PR-2 / PR-3 (deductible / coinsurance / copay) out of CAS adjustments, in dollars. */
function sumStandardPatientResponsibility(
  adjustments: Array<{ group: string; code: string; amount: number }>
): IClaimPatientResponsibility {
  const prAdjustments = adjustments.filter((adj) => adj.group === "PR");

  // Sum amounts by code
  const deductible = prAdjustments
    .filter((adj) => adj.code === "1")
    .reduce((sum, adj) => sum + (adj.amount || 0), 0);
  const coinsurance = prAdjustments
    .filter((adj) => adj.code === "2")
    .reduce((sum, adj) => sum + (adj.amount || 0), 0);
  const copay = prAdjustments
    .filter((adj) => adj.code === "3")
    .reduce((sum, adj) => sum + (adj.amount || 0), 0);

  return {
    deductible,
    coinsurance,
    copay,
    totalOOP: deductible + coinsurance + copay,
  };
}

/**
 * Extract patient responsibility amounts from underpayment lineItems
 * 835 adjustment codes: 1=Deductible, 2=Coinsurance, 3=Copay
 * Only PR (Patient Responsibility) group adjustments are included
 *
 * @param lineItems - Array of line items from the underpayment
 * @returns Patient responsibility breakdown with amounts in dollars
 */
export function extractClaimPatientResponsibility(
  lineItems?: ILineItem[]
): IClaimPatientResponsibility {
  return sumStandardPatientResponsibility(
    (lineItems || []).flatMap((line) => line.adjustments || [])
  );
}

/**
 * CLE-2804 — a claim's patient responsibility, read from wherever the remit put it.
 *
 * Service lines win whenever the claim has any. A header-only remit (CLP + claim-level CAS, no
 * SVC lines — outpatient institutional claims as well as DRG inpatient ones) carries its PR-1 /
 * PR-2 / PR-3 only in `claimSummaryData.adjustments`, so reading lines alone left the Copay /
 * Deductible / Coinsurance columns blank for it (prod A2969V1P71: PR-3 $150 missing).
 *
 * This is the backend's "lines, else summary" rule for the claim's allowed amount
 * (`calculateEffectiveClaimAmountFromSummary`), so Coinsurance % and OOP % stay consistent with
 * Allowed. The two sources are never summed: a claim with lines ignores its claim-level PR.
 */
export function getClaimPatientResponsibility(claim: {
  isEpisodeRoot?: boolean;
  episodeNetLineItems?: ILineItem[] | null;
  lineItems?: ILineItem[] | null;
  claimSummaryData?: {
    adjustments?: Array<{ group: string; code: string; amount: number }>;
  } | null;
}): IClaimPatientResponsibility {
  const lines = getEffectiveClaimLineItems(claim);
  if (lines.length > 0) return extractClaimPatientResponsibility(lines);
  return sumStandardPatientResponsibility(
    claim.claimSummaryData?.adjustments ?? []
  );
}

/**
 * A single line's effective allowed = insurance paid + standard patient responsibility
 * (deductible PR-1, coinsurance PR-2, copay PR-3) + the QMB write-off (CARC 303), in dollars.
 *
 * Mirrors the backend `effectiveLinePaid` (edi_conversion.helpers.ts) and the claim-level
 * `claimAmount`: it intentionally EXCLUDES non-standard PR codes (e.g. PR-26 "expenses
 * prior to coverage", PR-97 bundled-into-another-service) so a payer that mis-codes a
 * bundled line as patient responsibility doesn't inflate the allowed. Use this — not the
 * raw `lineAllowed` (which includes all PR) — so the per-line allowed matches claim review.
 *
 * CLE-2812: CARC 303 is cost-share a provider may not bill to a Qualified Medicare Beneficiary.
 * The payer allowed it and barred collection, so it counts toward allowed like PR-1/2/3. Matched
 * on the code (X12 puts it in CO) — it appears in no PR list, so it cannot double-count.
 *
 * CLE-2434 / CLE-2953: a spending-account payment (PR-187, FSA/HRA) sits in BOTH `linePaid` and a
 * (usually negative) PR-187 adjustment offsetting the PR-1/2/3 it paid, so it is added SIGNED —
 * otherwise the FSA/HRA payment is double-counted (prod 573393V14254: 80053 paid $7.92 + PR-1 $7.92
 * + PR-187 -$7.92 = $7.92 allowed, not $15.84).
 */
export function effectiveLineAllowed(line: ILineItem): number {
  const paid = line.linePaid || 0;
  const standardPr = (line.adjustments || [])
    .filter(
      (adj) =>
        adj.group === "PR" &&
        (adj.code === "1" || adj.code === "2" || adj.code === "3")
    )
    .reduce((sum, adj) => sum + (adj.amount || 0), 0);
  const spendingAccount = (line.adjustments || [])
    .filter((adj) => adj.group === "PR" && adj.code === "187")
    .reduce((sum, adj) => sum + (adj.amount || 0), 0);
  const qmbWriteOff = (line.adjustments || [])
    .filter((adj) => adj.code === "303")
    .reduce((sum, adj) => sum + (adj.amount || 0), 0);
  return paid + standardPr + spendingAccount + qmbWriteOff;
}

/**
 * Calculate OOP percentage
 * Formula: patientOOP / (allowedAmount - deductible) * 100
 *
 * @param patientOOP - Total patient out-of-pocket in cents
 * @param allowedAmount - Total allowed amount in cents
 * @param deductible - Patient deductible in cents
 * @returns OOP percentage or null if calculation not possible
 */
export function calculateOOPPercent(
  patientOOP: number,
  allowedAmount: number,
  deductible: number
): number | null {
  const denominator = allowedAmount - deductible;
  if (denominator <= 0) return null;
  return (patientOOP / denominator) * 100;
}

/**
 * Format currency amount from cents to dollars string
 * @param amountInCents - Amount in cents
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrency(
  amountInCents: number | null | undefined
): string {
  if (amountInCents === null || amountInCents === undefined) {
    return "-";
  }
  const dollars = amountInCents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/**
 * Format percentage value
 * @param percent - Percentage value
 * @returns Formatted percentage string (e.g., "12.5%")
 */
export function formatPercent(percent: number | null | undefined): string {
  if (percent === null || percent === undefined) {
    return "-";
  }
  return `${percent.toFixed(1)}%`;
}

/**
 * Format dollar amount (no conversion needed)
 * Use this for 835 EDI data which is already in dollars
 * @param amountInDollars - Amount in dollars
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatDollars(
  amountInDollars: number | null | undefined
): string {
  if (amountInDollars === null || amountInDollars === undefined) {
    return "-";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInDollars);
}

/**
 * Check if a CPT code is an implant: HCPCS device pass-through C1xxx-C2xxx only. C3xxx-C9xxx are
 * procedures, drugs, imaging and new technology, not implants (CLE-2946). Mirrors the API's
 * isImplantCptCode in services/clear-api/src/domains/claims/utils/implant-detection.util.ts.
 */
export function isImplantCptCode(cptCode: string | null | undefined): boolean {
  if (!cptCode) return false;
  return /^C[12]\d{3}$/i.test(cptCode);
}

/**
 * Generate case signature from estimate procedures
 * Matches the backend logic: aggregates CPT codes with quantities, excludes implants,
 * sorts alphabetically, format: "CPT1:QTY1,CPT2:QTY2,..."
 *
 * Only includes FACILITY procedures to match 835 claim case signatures
 */
export function generateEstimateCaseSignature(
  order: IMatchedOrder | null | undefined
): string {
  if (!order) return "";

  const pricingBreakdown = order.gfeQuote?.pricingBreakdown as any;
  if (!pricingBreakdown?.procedureBreakdown?.length) return "";

  // Filter to FACILITY procedures, exclude implants, aggregate quantities by CPT
  const facilityProcedures = pricingBreakdown.procedureBreakdown.filter(
    (proc: any) => proc.specialty === "FACILITY" && !isImplantCptCode(proc.code)
  );

  // Aggregate quantities by CPT code
  const cptQuantities = facilityProcedures.reduce(
    (acc: Record<string, number>, proc: any) => {
      const cptCode = proc.code;
      acc[cptCode] = (acc[cptCode] || 0) + 1;
      return acc;
    },
    {}
  );

  // Convert to array, filter zeros, sort alphabetically, format as CPT:QTY pairs
  return Object.entries(cptQuantities)
    .filter(([, qty]) => qty !== 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cpt, qty]) => `${cpt}:${qty}`)
    .join(",");
}
